/**
 * AdminMasterExperiencesPage (Iter 9.39a - table refresh)
 * Master list of experience types (e.g. Style experience, Food experience).
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import MasterDataTable from "@/components/admin/MasterDataTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const CATEGORY_OPTIONS = [
  "hair", "makeup", "nails", "lashes", "culinary", "style",
  "wellness", "events", "dispensary", "instructor",
];

export default function AdminMasterExperiencesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inspoFolders, setInspoFolders] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/master-experiences`, { headers: auth() });
      if (r.ok) { const d = await r.json(); setItems(d.experiences || d || []); }
      // Load inspo folders for binding dropdown
      const rf = await fetch(`${API}/admin/master-inspo/folders`, { headers: auth() });
      if (rf.ok) { const df = await rf.json(); setInspoFolders(df.folders || []); }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    if (!window.confirm("Delete?")) return;
    await fetch(`${API}/admin/master-experiences/${row.experience_id || row.id}`, { method: "DELETE", headers: auth() });
    load();
  };

  const columns = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium text-gray-900">{r.name}</span> },
    { key: "verb", label: "Verb", render: (r) => r.verb ? <span className="text-gray-700 italic">"{r.verb}"</span> : <span className="text-gray-300">-</span> },
    { key: "category", label: "Category", render: (r) => r.category ? <Badge variant="outline" className="text-[10px] capitalize">{r.category}</Badge> : <span className="text-gray-300">-</span> },
    { key: "preference_fields", label: "Linked preferences", render: (r) => {
      const f = r.preference_fields || r.linked_preference_fields || [];
      if (!f.length) return <span className="text-gray-300 text-[11px]">-</span>;
      return (
        <div className="flex flex-wrap gap-1 max-w-[280px]">
          {f.slice(0, 4).map(k => (
            <span key={k} className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded">{k}</span>
          ))}
          {f.length > 4 && <span className="text-[10px] text-gray-400">+{f.length - 4}</span>}
        </div>
      );
    }},
    { key: "inspo_folder", label: "Inspo folder", render: (r) => {
      const fid = r.inspo_folder_id || r.linked_inspo_folder_id;
      if (!fid) return <span className="text-gray-300 text-[11px]">-</span>;
      return <Badge variant="secondary" className="text-[10px]">{fid}</Badge>;
    }},
    { key: "description", label: "Card description", render: (r) => r.description ? <span className="text-[11px] text-stone-500 line-clamp-2">{r.description}</span> : <span className="text-gray-300">-</span> },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-master-experiences-page">
      <MasterDataTable
        title="Master Experience Types"
        icon={Sparkles}
        description={`e.g. retail = "Style experience", culinary = "Food". Bound to category in template editor.`}
        loading={loading}
        rows={items}
        columns={columns}
        searchFields={["name", "verb"]}
        testid="master-exp"
        renderCreateForm={({ onCreated }) => <ExperienceCreateForm onCreated={() => { onCreated(); load(); }} inspoFolders={inspoFolders} />}
        onDelete={remove}
        emptyHint="No master experience types yet."
      />
    </div>
  );
}

function ExperienceCreateForm({ onCreated, inspoFolders = [] }) {
  const [draft, setDraft] = useState({ name: "", verb: "", category: "", description: "", preference_fields: "", inspo_folder_id: "" });

  const create = async () => {
    if (!draft.name) return toast.error("Name required");
    const body = {
      ...draft,
      preference_fields: draft.preference_fields ? draft.preference_fields.split(",").map(s => s.trim()).filter(Boolean) : [],
    };
    const r = await fetch(`${API}/admin/master-experiences`, {
      method: "POST",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (r.ok) { toast.success("Experience added"); onCreated && onCreated(); }
    else toast.error("Save failed");
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div><Label className="text-xs">Name</Label><Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} data-testid="exp-name" placeholder="e.g. Style experience" /></div>
        <div><Label className="text-xs">Verb (CTA copy)</Label><Input value={draft.verb} onChange={e => setDraft(d => ({ ...d, verb: e.target.value }))} data-testid="exp-verb" placeholder="Plan a style session" /></div>
        <div>
          <Label className="text-xs">Category</Label>
          <select value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm" data-testid="exp-category">
            <option value="">-</option>
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Card description (drives the preference card body shown on /me)</Label>
        <Input value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} data-testid="exp-desc" placeholder="Save your style preferences once and share them with any host." />
      </div>
      <div>
        <Label className="text-xs">Linked preference fields (comma-separated field_ids)</Label>
        <Input value={draft.preference_fields} onChange={e => setDraft(d => ({ ...d, preference_fields: e.target.value }))} data-testid="exp-pref-fields" placeholder="hair_type, hair_length, hair_color" />
        <p className="text-[10px] text-stone-400 mt-1">These are the preference fields that appear inside this experience's preference card on /me. Pull from Master Preferences field_ids.</p>
      </div>
      <div>
        <Label className="text-xs">Inspo folder (synced from Master Inspo)</Label>
        <select value={draft.inspo_folder_id} onChange={e => setDraft(d => ({ ...d, inspo_folder_id: e.target.value }))} className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm" data-testid="exp-inspo-folder">
          <option value="">- No folder -</option>
          {inspoFolders.map(f => (
            <option key={f.folder_id} value={f.folder_id}>{f.category} · {f.name}</option>
          ))}
        </select>
        <p className="text-[10px] text-stone-400 mt-1">Which Master Inspo folder supplies the "final look" gallery when this experience is planned.</p>
      </div>
      <Button onClick={create} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="exp-save"><Plus className="w-3.5 h-3.5 mr-1" />Add experience type</Button>
    </div>
  );
}
